'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/_components/DonationLayout/DonationSubHeader';
import type { GiftSaleStatus } from '@/app/donation/types';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TABS } from '../../constants';
import type { GiftDetailPageProps } from '../../page';
import { GiftDetailTab } from '../GiftDetailTab';
import { GiftTabContentBasic } from '../GiftTabContent/_components/GiftTabContentBasic';
import { GiftTabContentDelivery } from '../GiftTabContent/_components/GiftTabContentDelivery';
import { GiftTabContentInfo } from '../GiftTabContent/_components/GiftTabContentInfo';
import { GiftTabContentInquiry } from '../GiftTabContent/_components/GiftTabContentInquiry';
import { GiftTabContentReview } from '../GiftTabContent/_components/GiftTabContentReview';
import { HeaderInfoSection } from './HeaderInfoSection';
import { PrefillForm } from './PrefillForm';
import { ResponsiveDivider } from './ResponsiveDivider';
import { ReviewShortcutSection } from './ReviewShortcutSection';
interface GiftDetailTemplateProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionId: number;
  institutionLogoUrl: string;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  searchParams?: GiftDetailPageProps['searchParams'];
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  reviewList: GiftOrderProductReviewDto[];
  reviewTotalCount: number;
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  detailContentInfoList: GiftProductDetail['detailContentInfoList'];
  productInfo: string;
  deliveryCompanyName?: string;
  providerName?: string;
  providerBusinessNumber?: string;
  providerContactNumber?: string;
  providerAddress?: string;
  productData?: GiftProductDetail;
  noticeTitle?: string;
  noticeContents?: string;
}
const GIFT_DETAIL_SIDEBAR_STICKY_TOP = 218;
export const GiftDetailTemplate = t0 => {
  const $ = _c(79);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionId,
    institutionLogoUrl,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    searchParams,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    reviewList,
    reviewTotalCount,
    photoReviewList,
    photoReviewCount,
    detailContentInfoList,
    productInfo,
    deliveryCompanyName,
    providerName,
    providerBusinessNumber,
    providerContactNumber,
    providerAddress,
    productData,
    noticeTitle,
    noticeContents
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t1;
  if ($[0] !== reviewCount) {
    t1 = [...(reviewCount < 1 ? [TABS.REVIEW] : [])];
    $[0] = reviewCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const disabledTabs = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("mt-20 content-container", "tablet:mt-0 tablet:px-0", "mobile:mt-0 mobile:px-0");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("mb-100 px-40", "mobile-tablet:mb-20 mobile-tablet:px-0");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== adultYN || $[5] !== exchangeYN || $[6] !== heroImageUrlList || $[7] !== institutionId || $[8] !== institutionLogoUrl || $[9] !== institutionName || $[10] !== onlineProductDesc || $[11] !== onlineProductYN || $[12] !== productData || $[13] !== productName || $[14] !== productNumber || $[15] !== productSaleStatus || $[16] !== requiredDonationAmount || $[17] !== reviewCount || $[18] !== reviewRating || $[19] !== title) {
    t4 = <HeaderInfoSection title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} institutionId={institutionId} institutionLogoUrl={institutionLogoUrl} institutionName={institutionName} productSaleStatus={productSaleStatus} productName={productName} reviewCount={reviewCount} reviewRating={reviewRating} productNumber={productNumber} requiredDonationAmount={requiredDonationAmount} exchangeYN={exchangeYN} onlineProductYN={onlineProductYN} onlineProductDesc={onlineProductDesc} productData={productData} className={t3} />;
    $[4] = adultYN;
    $[5] = exchangeYN;
    $[6] = heroImageUrlList;
    $[7] = institutionId;
    $[8] = institutionLogoUrl;
    $[9] = institutionName;
    $[10] = onlineProductDesc;
    $[11] = onlineProductYN;
    $[12] = productData;
    $[13] = productName;
    $[14] = productNumber;
    $[15] = productSaleStatus;
    $[16] = requiredDonationAmount;
    $[17] = reviewCount;
    $[18] = reviewRating;
    $[19] = title;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("mb-100 px-40", "mobile-tablet:hidden");
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== reviewCount || $[23] !== reviewList) {
    t6 = <ReviewShortcutSection reviewCount={reviewCount} items={reviewList} className={t5} />;
    $[22] = reviewCount;
    $[23] = reviewList;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("sticky z-10 mx-40 bg-Gray-white", "mobile-tablet:top-[48px] mobile-tablet:mx-0");
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isDesktopViewport) {
    t8 = isDesktopViewport ? {
      top: DONATION_DESKTOP_STICKY_TOP
    } : undefined;
    $[26] = isDesktopViewport;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== disabledTabs || $[29] !== productNumber || $[30] !== t8) {
    t9 = <GiftDetailTab productNumber={productNumber} disabledTabs={disabledTabs} className={t7} style={t8} />;
    $[28] = disabledTabs;
    $[29] = productNumber;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("mt-40 grid grid-cols-[1fr_370px] gap-80 px-40", "mobile-tablet:mt-0 mobile-tablet:grid-cols-1 mobile-tablet:px-0");
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== detailContentInfoList || $[34] !== noticeContents || $[35] !== noticeTitle || $[36] !== productName) {
    t11 = <GiftTabContentBasic detailContentInfoList={detailContentInfoList} productName={productName} noticeTitle={noticeTitle} noticeContents={noticeContents} />;
    $[33] = detailContentInfoList;
    $[34] = noticeContents;
    $[35] = noticeTitle;
    $[36] = productName;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ResponsiveDivider />;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== productInfo) {
    t13 = <GiftTabContentInfo productInfo={productInfo} />;
    $[39] = productInfo;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ResponsiveDivider />;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== deliveryCompanyName || $[43] !== providerAddress || $[44] !== providerBusinessNumber || $[45] !== providerContactNumber || $[46] !== providerName) {
    t15 = <GiftTabContentDelivery deliveryCompanyName={deliveryCompanyName} providerName={providerName} providerBusinessNumber={providerBusinessNumber} providerContactNumber={providerContactNumber} providerAddress={providerAddress} />;
    $[42] = deliveryCompanyName;
    $[43] = providerAddress;
    $[44] = providerBusinessNumber;
    $[45] = providerContactNumber;
    $[46] = providerName;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ResponsiveDivider />;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== photoReviewCount || $[50] !== photoReviewList || $[51] !== productNumber || $[52] !== reviewCount || $[53] !== reviewList || $[54] !== reviewRating || $[55] !== reviewTotalCount || $[56] !== searchParams) {
    t17 = <GiftTabContentReview reviewRating={reviewRating} reviewCount={reviewCount} reviewList={reviewList} reviewTotalCount={reviewTotalCount} photoReviewList={photoReviewList} photoReviewCount={photoReviewCount} productNumber={productNumber} searchParams={searchParams} />;
    $[49] = photoReviewCount;
    $[50] = photoReviewList;
    $[51] = productNumber;
    $[52] = reviewCount;
    $[53] = reviewList;
    $[54] = reviewRating;
    $[55] = reviewTotalCount;
    $[56] = searchParams;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ResponsiveDivider />;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== productNumber) {
    t19 = <GiftTabContentInquiry productNumber={productNumber} />;
    $[59] = productNumber;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== t11 || $[62] !== t13 || $[63] !== t15 || $[64] !== t17 || $[65] !== t19) {
    t20 = <div className="flex min-w-0 flex-col">{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}</div>;
    $[61] = t11;
    $[62] = t13;
    $[63] = t15;
    $[64] = t17;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      top: GIFT_DETAIL_SIDEBAR_STICKY_TOP
    };
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== productData || $[69] !== productNumber) {
    t22 = <div className="mobile:hidden tablet:hidden"><div className="sticky mb-44 rounded-[36px] border-1 px-28 py-44" style={t21}><PrefillForm productData={productData} productNumber={productNumber} className="gap-80" /></div></div>;
    $[68] = productData;
    $[69] = productNumber;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== t20 || $[72] !== t22) {
    t23 = <div className={t10}>{t20}{t22}</div>;
    $[71] = t20;
    $[72] = t22;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== t23 || $[75] !== t4 || $[76] !== t6 || $[77] !== t9) {
    t24 = <div className={t2}>{t4}{t6}{t9}{t23}</div>;
    $[74] = t23;
    $[75] = t4;
    $[76] = t6;
    $[77] = t9;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  return t24;
};